/**
 * The Forsthofgut Akademie's fields on a course, which is a service carrying `academy_levels`.
 *
 * Keys mirror `AcademyTags.kt`. One rule here differs from the profile: on a service, the presence
 * of `academy_levels` is what makes it a course at all (`isAcademyCourse`), so clearing every level
 * must keep the key as "" instead of removing it. An empty set means open to every team member, which
 * is what the API and the app read it as; a removed key would quietly turn the course back into an
 * ordinary service.
 */
import type {Service} from "crqlar-react-common/dist/models/service/service.model";
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import type {DataTags} from "../common/dataTags.util";
import {academyTagText, joinList, withAcademyTag} from "../common/academyTags.util";

export const AcademyCourseTags = {
    CREDITS: 'academy_credits',
    LEVELS: 'academy_levels',
    COURSE_VALUE: 'academy_course_value',
    TRAINER: 'academy_trainer',
    ROOM: 'academy_room',
} as const

export const isAcademyCourse = (tags: DataTags | null | undefined): boolean =>
    !!tags && Object.prototype.hasOwnProperty.call(tags, AcademyCourseTags.LEVELS)

/**
 * Show the course section for a service in the Akademie sector, or one that is already a course.
 *
 * The second half keeps a course editable even if somebody moves it to another category.
 */
export const showsAcademyCourse = (
    akademie: Sector | undefined,
    sectorIds: string[],
    tags: DataTags | null | undefined,
): boolean => !!akademie && (sectorIds.includes(akademie.id as string) || isAcademyCourse(tags))

/** Levels written back, keeping the key when none is ticked; see the note at the top. */
export const withCourseLevels = (tags: DataTags | null | undefined, levels: string[]): DataTags =>
    ({...(tags ?? {}), [AcademyCourseTags.LEVELS]: joinList(levels)})

/**
 * Credits as typed into the field: whole numbers from 0 up, stored as digits.
 *
 * 0 is a real value, part one of a two-part Level 3 course, and is kept. Anything that is not a
 * whole number leaves the tags unchanged, so a half-typed value never reaches the API.
 */
export const withCourseCredits = (tags: DataTags | null | undefined, input: string): DataTags => {
    const trimmed = input.trim()
    if (trimmed === '') return withAcademyTag(tags, AcademyCourseTags.CREDITS, undefined)
    if (!/^\d+$/.test(trimmed)) return {...(tags ?? {})}
    return withAcademyTag(tags, AcademyCourseTags.CREDITS, String(parseInt(trimmed, 10)))
}

/**
 * Trainers or rooms already used on other courses, to pick from instead of retyping.
 *
 * Retyping is how one trainer becomes two in the statistics ("Nina Beyrl" and "Nina  Beyrl").
 */
export const usedCourseValues = (services: Service[], key: string): string[] =>
    Array.from(new Set(
        services
            .filter(service => isAcademyCourse(service.tags))
            .map(service => academyTagText(service.tags, key))
            .filter((value): value is string => !!value)
    )).sort((a, b) => a.localeCompare(b))
